import { useState, useEffect } from 'react'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { ADD_APP_BANNER, UPDATE_APP_BANNER } from 'graphql/common/mutations'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { FileAttachment } from 'components/custom/FileAttachment'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_APP_BANNERS, GET_COUNTRY_LIST } from 'graphql/common/queries'
import Flatpickr from 'react-flatpickr'

export default function AppBannerForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addBannerError, setAddBannerError] = useState(t('Problem Adding Banner'))
    const [submitLoading, setSubmitLoading] = useState(false)
    const [bannerInfo, setBannerInfo] = useState(null)
    const [bannerMobAttachments, setBannerMobAttachments] = useState([])
    const [bannerTabAttachments, setBannerTabAttachments] = useState([])
    const [countries, setCountries] = useState([])

    const handleBack = () => {
        navigate('/app-banners')
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    // Query to get country list
    const { data: countryData } = useQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
    })

    // Query to get Banner Details by id
    const [getBannerById, { loading: bannerListLoading, error: bannerListError }] = useLazyQuery(GET_APP_BANNERS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getAppBanners.success && data.getAppBanners.code === '200') {
                const banner = data.getAppBanners.app_banners[0]
                setBannerInfo(banner)
                // for mobile banner
                setBannerMobAttachments([
                    {
                        file_name: banner.image.mobile.file_name,
                        size: banner.image.mobile.size,
                        mime_type: banner.image.mobile.mime_type,
                        s3_key: banner.image.mobile.s3_key,
                    },
                ])
                // for tablet banner
                setBannerTabAttachments([
                    {
                        file_name: banner.image.tablet.file_name,
                        size: banner.image.tablet.size,
                        mime_type: banner.image.tablet.mime_type,
                        s3_key: banner.image.tablet.s3_key,
                    },
                ])
            }
        },
    })
    useEffect(() => {
        if (id) {
            searchBanner()
        } else {
            setBannerInfo(null)
            CreateBannerFormValidation.resetForm()
        }
    }, [id])
    // Search users User List
    const searchBanner = () => {
        getBannerById({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                bannerFilter: {
                    id: id || null,
                    valid_from: null,
                    valid_to: null,
                    is_active: null,
                },
            },
        })
    }
    // Get Banner Details by id

    //  MUtation Add Banner
    const [addNewBanner, { loading: addLoading }] = useMutation(ADD_APP_BANNER, {
        onCompleted: data => {
            if (data?.createAppBanner.code === '200' && data?.createAppBanner.success === true) {
                CreateBannerFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 1500)
            } else {
                CreateBannerFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddBannerError(data?.createAppBanner.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateBannerFormValidation.setSubmitting(false)
            setAddBannerError(error.message)
            setShowErrDialog(true)
        },
    })
    // Mutation Update Banner
    const [updateBanner, { loading: updateLoading }] = useMutation(UPDATE_APP_BANNER, {
        onCompleted: data => {
            if (data?.updateAppBanner.code === '200' && data?.updateAppBanner.success === true) {
                CreateBannerFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 1500)
            } else {
                CreateBannerFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddBannerError(data?.updateBanner.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateBannerFormValidation.setSubmitting(false)
            setAddBannerError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleMobAttachmentsChange = attachments => {
        setBannerMobAttachments(attachments)
    }
    const handleTabAttachmentsChange = attachments => {
        setBannerTabAttachments(attachments)
    }
    // Form Validation
    const CreateBannerFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            valid_from: bannerInfo?.valid_from || '',
            valid_to: bannerInfo?.valid_to || '',
            image: bannerInfo?.image || {},
            is_active: bannerInfo?.is_active ? 'true' : 'false',
            category: bannerInfo?.category || 'APP_BANNER',
            country_code: bannerInfo?.country_code || '',
            banner_info: {
                banner_name: bannerInfo?.banner_info?.banner_name || '',
                title: {
                    en: bannerInfo?.banner_info?.title?.en || '',
                    ar: bannerInfo?.banner_info?.title?.ar || '',
                },
                description: {
                    en: bannerInfo?.banner_info?.description?.en || '',
                    ar: bannerInfo?.banner_info?.description?.ar || '',
                },
                trip_tag: bannerInfo?.banner_info?.trip_tag || '',
                link_url: bannerInfo?.banner_info?.link_url || '',
            },
        },
        validationSchema: Yup.object().shape({
            valid_from: Yup.string().required(t('Valid from required')),
            valid_to: Yup.string().required(t('Valid to required')),
            is_active: Yup.boolean().required(t('Required')),
            category: Yup.string().required(t('Category required')),
            country_code: Yup.string().required(t('Country code required')),
            banner_info: Yup.object().shape({
                title: Yup.object().shape({
                    en: Yup.string(),
                    ar: Yup.string(),
                }),
                description: Yup.object().shape({
                    en: Yup.string(),
                    ar: Yup.string(),
                }),
                trip_tag: Yup.string(),
                link_url: Yup.string().url(t('Must be a valid URL')),
                banner_name: Yup.string(),

            }),
        }),
        onSubmit: values => {
            setSubmitLoading(true)
            submitBanner(values)
        },
    })

    const submitBanner = async bannerInfo => {
        try {
            CreateBannerFormValidation.setSubmitting(true)
            //remove __typename from image
            if (bannerMobAttachments.length === 0 || bannerTabAttachments.length === 0) {
                setSubmitLoading(false)
                CreateBannerFormValidation.setSubmitting(false)
                setAddBannerError('Please upload image')
                setShowErrDialog(true)
                return
            }
            const formattedData = {
                appBanner: {
                    image: {
                        mobile: {
                            file_name: bannerMobAttachments[0].file_name,
                            size: bannerMobAttachments[0].size,
                            mime_type: bannerMobAttachments[0].mime_type,
                            s3_key: bannerMobAttachments[0].s3_key,
                        },

                        tablet: {
                            file_name: bannerTabAttachments[0].file_name,
                            size: bannerTabAttachments[0].size,
                            mime_type: bannerTabAttachments[0].mime_type,
                            s3_key: bannerTabAttachments[0].s3_key,
                        },
                    },
                    valid_from: bannerInfo.valid_from,
                    valid_to: bannerInfo.valid_to,
                    is_active: bannerInfo.is_active === 'true',
                    category: bannerInfo.category,
                    country_code: bannerInfo.country_code,
                },
            }

            // Check if any banner content is provided
            const hasAnyBannerContent =
                bannerInfo.banner_info.banner_name ||
                bannerInfo.banner_info.title.en || 
                bannerInfo.banner_info.title.ar || 
                bannerInfo.banner_info.description.en || 
                bannerInfo.banner_info.description.ar ||
                bannerInfo.banner_info.trip_tag ||
                bannerInfo.banner_info.link_url;

            // Only include banner_info if any content is provided
            if (hasAnyBannerContent) {
                formattedData.appBanner.banner_info = {
                    banner_name: bannerInfo.banner_info.banner_name || null,
                    title: {
                        en: bannerInfo.banner_info.title.en || null,
                        ar: bannerInfo.banner_info.title.ar || null,
                    },
                    description: {
                        en: bannerInfo.banner_info.description.en || null,
                        ar: bannerInfo.banner_info.description.ar || null,
                    },
                    trip_tag: bannerInfo.banner_info.trip_tag || null,
                    link_url: bannerInfo.banner_info.link_url || null,
                };
            }

            if (id) {
                formattedData['updateAppBannerId'] = id
                updateBanner({
                    variables: formattedData,
                })
            } else {
                addNewBanner({
                    variables: formattedData,
                })
            }
        } catch (error) {
            CreateBannerFormValidation.setSubmitting(false)
        }
    }

    return (
        <>
            <div>
                <div className="mb-8 flex justify-between items-center">
                    <div className="flex ">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                        >
                            <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                            {t('Back')}
                        </button>
                        <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1"> {t('App Banner details')}</h3>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={CreateBannerFormValidation.isSubmitting}
                            onClick={CreateBannerFormValidation.handleSubmit}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
                <form>
                    <div className="space-y-12">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Banner Information')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of the banner.')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.banner_name" className="block text-sm font-medium text-gray-900">
                                        {t('Banner Name')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="banner_info.banner_name"
                                        name="banner_info.banner_name"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.banner_name}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.banner_name &&
                                        CreateBannerFormValidation.errors.banner_info?.banner_name && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.banner_name}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="valid_from" className="block text-sm font-medium text-gray-900">
                                        {t('Valid From')}
                                    </label>
                                    <Flatpickr
                                        id="valid_from"
                                        name="valid_from"
                                        placeholder="yyyy-mm-dd"
                                        value={CreateBannerFormValidation.values.valid_from}
                                        onChange={date =>
                                            CreateBannerFormValidation.setFieldValue(
                                                'valid_from',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            dateFormat: 'Y/m/d', // Date format to display as 'YYYY/MM/DD'
                                            maxDate: '2050-12-31',
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            minDate: (id != null && new Date(CreateBannerFormValidation.values.valid_from) <= new Date()) ? CreateBannerFormValidation.values.valid_from : 'today',

                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />

                                    {CreateBannerFormValidation.touched.valid_from &&
                                        CreateBannerFormValidation.errors.valid_from && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.valid_from}
                                            </p>
                                        )}

                                    {/* <input
                                        type="date"
                                        id="valid_from"
                                        name="valid_from"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.valid_from}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"

                                    /> */}
                                    {/* {CreateBannerFormValidation.touched.valid_from &&
                                        CreateBannerFormValidation.errors.valid_from && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.valid_from}
                                            </p>
                                        )} */}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="valid_to" className="block text-sm font-medium text-gray-900">
                                        {t('Valid To')}
                                    </label>

                                    <Flatpickr
                                        id="valid_to"
                                        name="valid_to"
                                        placeholder="yyyy-mm-dd"
                                        value={CreateBannerFormValidation.values.valid_to}
                                        onChange={date =>
                                            CreateBannerFormValidation.setFieldValue(
                                                'valid_to',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            dateFormat: 'Y/m/d', // Date format to display as 'YYYY/MM/DD'
                                            maxDate: '2050-12-31',
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            minDate: CreateBannerFormValidation.values.valid_from || 'today',
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />

                                    {/* <input
                                        type="date"
                                        id="valid_to"
                                        name="valid_to"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.valid_to}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    /> */}
                                    {CreateBannerFormValidation.touched.valid_to &&
                                        CreateBannerFormValidation.errors.valid_to && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.valid_to}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <FileAttachment
                                        id="mobile_banner"
                                        name="mobile_banner"
                                        maxLength={1}
                                        height="130"
                                        uploadLocation="public/app_banner"
                                        title={t('Mobile Banner Images')}
                                        onChange={handleMobAttachmentsChange}
                                        attachments={bannerMobAttachments}
                                        isRequired={true}
                                        isDisabled={bannerMobAttachments && bannerMobAttachments.length > 0}
                                        // fileSupported={['image/jpeg', 'image/png', 'image/jpg']}
                                        maxSize={1}
                                    ></FileAttachment>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {CreateBannerFormValidation.values.category === 'APP_BANNER' 
                                            ? t('Recommended: 398 x 189 px')
                                            : t('Recommended: 1080 × 1920 px (9:16 ratio)')}
                                    </p>
                                </div>
                                <div className="sm:col-span-6">
                                    <FileAttachment
                                        id="tablet_banner"
                                        name="tablet_banner"
                                        maxLength={1}
                                        height="130"
                                        uploadLocation="public/app_banner"
                                        title={t('Tablet Banner Images')}
                                        onChange={handleTabAttachmentsChange}
                                        attachments={bannerTabAttachments}
                                        isRequired={true}
                                        isDisabled={bannerTabAttachments && bannerTabAttachments.length > 0}
                                        // fileSupported={['image/jpeg', 'image/png', 'image/jpg']}
                                        maxSize={1}
                                    ></FileAttachment>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {CreateBannerFormValidation.values.category === 'APP_BANNER' 
                                            ? t('Recommended: 1185 x 239 px')
                                            : t('Recommended: 1080 × 1920 px (9:16 ratio)')}
                                    </p>
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="category" className="block text-sm font-medium text-gray-900">
                                        {t('Category')} <span className="text-red">*</span>
                                    </label>
                                    <select
                                        id="category"
                                        name="category"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.category}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    >
                                        <option value="APP_BANNER">{t('App Banner')}</option>
                                        <option value="IN_APP">{t('Promotion Popup')}</option>
                                    </select>
                                    {CreateBannerFormValidation.touched.category &&
                                        CreateBannerFormValidation.errors.category && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.category}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="country_code" className="block text-sm font-medium text-gray-900">
                                        {t('Country Code')} <span className="text-red">*</span>
                                    </label>
                                    <select
                                        id="country_code"
                                        name="country_code"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.country_code}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    >
                                        <option value="">{t('Select Country')}</option>
                                        {countries.map(country => (
                                            <option key={country.alpha_iso2} value={country.alpha_iso2}>
                                                {country.country} ({country.alpha_iso2})
                                            </option>
                                        ))}
                                    </select>
                                    {CreateBannerFormValidation.touched.country_code &&
                                        CreateBannerFormValidation.errors.country_code && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.country_code}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="is_active" className="block text-sm font-medium text-gray-900">
                                        {t('Is Active')}
                                    </label>
                                    <select
                                        id="is_active"
                                        name="is_active"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.is_active}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    >
                                        <option value="">{t('Select')}</option>
                                        <option value="true">{t('Yes')}</option>
                                        <option value="false">{t('No')}</option>
                                    </select>
                                    {CreateBannerFormValidation.touched.is_active &&
                                        CreateBannerFormValidation.errors.is_active && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.is_active}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Banner Content')} <span className="text-gray-500 text-xs">({t('Optional')})</span>
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide banner title, description, and links.')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.title.en" className="block text-sm font-medium text-gray-900">
                                        {t('Title (English)')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="banner_info.title.en"
                                        name="banner_info.title.en"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.title.en}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.title?.en &&
                                        CreateBannerFormValidation.errors.banner_info?.title?.en && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.title.en}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.title.ar" className="block text-sm font-medium text-gray-900">
                                        {t('Title (Arabic)')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="banner_info.title.ar"
                                        name="banner_info.title.ar"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.title.ar}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                        dir="rtl"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.title?.ar &&
                                        CreateBannerFormValidation.errors.banner_info?.title?.ar && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.title.ar}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.description.en" className="block text-sm font-medium text-gray-900">
                                        {t('Description (English)')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <textarea
                                        id="banner_info.description.en"
                                        name="banner_info.description.en"
                                        rows={3}
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.description.en}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.description?.en &&
                                        CreateBannerFormValidation.errors.banner_info?.description?.en && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.description.en}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.description.ar" className="block text-sm font-medium text-gray-900">
                                        {t('Description (Arabic)')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <textarea
                                        id="banner_info.description.ar"
                                        name="banner_info.description.ar"
                                        rows={3}
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.description.ar}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                        dir="rtl"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.description?.ar &&
                                        CreateBannerFormValidation.errors.banner_info?.description?.ar && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.description.ar}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.trip_tag" className="block text-sm font-medium text-gray-900">
                                        {t('Trip Tag')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="banner_info.trip_tag"
                                        name="banner_info.trip_tag"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.trip_tag}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.trip_tag &&
                                        CreateBannerFormValidation.errors.banner_info?.trip_tag && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.trip_tag}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label htmlFor="banner_info.link_url" className="block text-sm font-medium text-gray-900">
                                        {t('Link URL')} <span className="text-gray-500 text-xs">(Optional)</span>
                                    </label>
                                    <input
                                        type="url"
                                        id="banner_info.link_url"
                                        name="banner_info.link_url"
                                        onChange={CreateBannerFormValidation.handleChange}
                                        value={CreateBannerFormValidation.values.banner_info.link_url}
                                        placeholder="https://example.com"
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                    />
                                    {CreateBannerFormValidation.touched.banner_info?.link_url &&
                                        CreateBannerFormValidation.errors.banner_info?.link_url && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBannerFormValidation.errors.banner_info.link_url}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <div className="mt-10 mb-8 flex justify-between items-center">
                    {/* <a href="/banners" className="inline-flex items-center gap-x-1.5 rounded-md text-sm">
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                        Back
                    </a> */}
                    <p></p>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={CreateBannerFormValidation.isSubmitting}
                            onClick={CreateBannerFormValidation.handleSubmit}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </div>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={addBannerError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
